/**
 * MeasurePanel — AK-320 «Линейка на карте» v1.
 *
 * Плавающая панель с выбором 4 режимов (сегмент-контрол), live-результатом
 * (га крупно) и действиями «Очистить» / «Готово». Монтируется в v2.tsx под
 * styleReady, когда measureActive=true. Только @/components/ui/* + Lucide.
 *
 * Панель НЕ держит геометрию — она читает результат из measureStore (его
 * пишет MeasureController) и диспатчит смену режима / деактивацию.
 */
import { Ruler, Minus, Spline, Hexagon, Circle, Eraser, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useMeasureStore,
  type MeasureMode,
  type CircleMode,
} from '@/lib/map/measure/measureStore';

interface ModeDef {
  mode: MeasureMode;
  label: string;
  icon: React.ComponentType<{ size?: number | string }>;
}

const MODES: ModeDef[] = [
  { mode: 'line', label: 'Прямая', icon: Minus },
  { mode: 'path', label: 'Ломаная', icon: Spline },
  { mode: 'area', label: 'Площадь', icon: Hexagon },
  { mode: 'circle', label: 'Круг', icon: Circle },
];

const HINTS: Record<MeasureMode, string> = {
  line: 'Кликните две точки. Esc или «Готово» — завершить.',
  path: 'Кликайте точки. Двойной клик, Esc или «Готово» — завершить.',
  area: 'Обведите участок. Клик по первой точке, двойной клик, Esc или «Готово» — замкнуть.',
  circle: 'Кликните две точки. Esc или «Готово» — завершить.',
};

const CIRCLE_MODES: { value: CircleMode; label: string }[] = [
  { value: 'center', label: 'От центра' },
  { value: 'diameter', label: 'По диаметру' },
];

export function MeasurePanel() {
  const mode = useMeasureStore((s) => s.mode);
  const circleMode = useMeasureStore((s) => s.circleMode);
  const result = useMeasureStore((s) => s.result);
  const setMode = useMeasureStore((s) => s.setMode);
  const setCircleMode = useMeasureStore((s) => s.setCircleMode);
  const deactivate = useMeasureStore((s) => s.deactivate);
  const clear = useMeasureStore((s) => s.clear);
  const finalize = useMeasureStore((s) => s.finalize);

  return (
    <div
      data-testid="measure-panel"
      // Левый-верх канвы (не конфликтует с RightRail справа и легендой).
      style={{ position: 'absolute', top: 16, left: 16, zIndex: 32, width: 280 }}
      className="rounded-lg border border-border bg-background/95 backdrop-blur-sm shadow-lg p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Ruler size={16} />
          <span>Линейка</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Закрыть линейку"
          data-testid="measure-close"
          onClick={() => deactivate()}
        >
          <X size={16} />
        </Button>
      </div>

      {/* Сегмент-контрол из 4 режимов. */}
      <div
        role="tablist"
        aria-label="Режим измерения"
        className="grid grid-cols-4 gap-1 rounded-md bg-muted p-1 mb-2"
      >
        {MODES.map(({ mode: m, label, icon: Icon }) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={label}
              title={label}
              data-testid={`measure-mode-${m}`}
              data-active={active ? 'true' : undefined}
              onClick={() => setMode(m)}
              className={
                'flex flex-col items-center justify-center gap-1 rounded px-1 py-1 text-xs font-medium transition-colors ' +
                (active
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground')
              }
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Способ построения круга — только в режиме «Круг». */}
      {mode === 'circle' && (
        <div
          role="radiogroup"
          aria-label="Способ построения круга"
          className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1 mb-2"
        >
          {CIRCLE_MODES.map(({ value, label }) => {
            const active = circleMode === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`measure-circle-${value}`}
                data-active={active ? 'true' : undefined}
                onClick={() => setCircleMode(value)}
                className={
                  'rounded px-2 py-1 text-xs font-medium transition-colors ' +
                  (active
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground')
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Live-результат: га / расстояние крупно, контекст мелко. */}
      <div
        data-testid="measure-result"
        className="rounded-md border border-border bg-muted/40 px-3 py-2 mb-2 min-h-[52px] flex flex-col justify-center"
      >
        {result ? (
          <>
            <div className="text-xl font-bold tabular-nums text-foreground leading-tight">
              {result.primary}
            </div>
            {result.secondary && (
              <div className="text-xs text-muted-foreground tabular-nums mt-1">
                {result.secondary}
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-muted-foreground">{HINTS[mode]}</div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          data-testid="measure-clear"
          onClick={() => clear()}
        >
          <Eraser size={14} />
          Очистить
        </Button>
        <Button
          variant="default"
          size="sm"
          className="flex-1"
          data-testid="measure-done"
          onClick={() => finalize()}
        >
          <Check size={14} />
          Готово
        </Button>
      </div>
    </div>
  );
}
